'use client';

import { BoxCTA } from '@lichtblick/photon/atoms/box-cta/box-cta';
import { Headline } from '@lichtblick/photon/atoms/headline/headline';
import { RequiredHint } from '@lichtblick/photon/atoms/required-hint';
import { Textfield } from '@lichtblick/photon/molecules/textfield';
import { type FC, useActionState, useRef } from 'react';
import { ErrorBanner } from '@/components/error-banner/error-banner';
import { FormRow } from '@/components/form-box/form-box';
import formBoxStyles from '@/components/form-box/form-box.module.scss';
import { ZipAndCityInput } from '@/components/steps/inputs/zip-and-city-input/zip-and-city-input';
import type { OverviewState } from '@/components/steps/overview/energy-solutions/schema';
import { i18n } from '@/i18n';
import { PATTERNS } from '../../../../patterns';
import type { PersonalDataAction, PersonalDataActionState } from './actions';
import styles from './personal-data-form.module.css';

type PersonalDataFormProps = {
  action: PersonalDataAction;
  editingNotFinishedError: boolean;
  state: OverviewState;
  setIsEditing: (isEditing: boolean) => void;
  isEditing: boolean;
};

export const PersonalDataForm: FC<PersonalDataFormProps> = ({
  action,
  state,
  editingNotFinishedError,
  setIsEditing,
  isEditing,
}) => {
  const formRef = useRef<HTMLFormElement>(null);
  const [actionState, formAction, isPending] = useActionState<PersonalDataActionState, FormData>(
    async (previousState, formData) => {
      const result = await action(previousState, formData);
      if (result.success) {
        setIsEditing(false);
      }
      return result;
    },
    undefined,
  );

  const handleFormReset = () => {
    formRef?.current?.reset();
    setIsEditing(false);
  };

  return (
    <form ref={formRef} action={formAction} className={formBoxStyles.box}>
      <Headline size="s">Deine Daten</Headline>
      {isEditing && <RequiredHint />}
      <fieldset disabled={isPending} className={formBoxStyles.fieldset}>
        <FormRow desktopColumns="1fr-1fr">
          <Textfield
            autoComplete="given-name"
            data-testid={`text-firstName`}
            defaultValue={state.firstName}
            errorMessage={i18n.validation.missing}
            label="Vorname"
            name="firstName"
            pattern={PATTERNS.name}
            required
            type="text"
            readOnly={!isEditing}
          />

          <Textfield
            autoComplete="family-name"
            data-testid={`text-lastName`}
            defaultValue={state.lastName}
            errorMessage={i18n.validation.missing}
            label="Nachname"
            name="lastName"
            pattern={PATTERNS.name}
            required
            type="text"
            readOnly={!isEditing}
          />
        </FormRow>

        <FormRow desktopColumns="1fr-2fr">
          {!isEditing && (
            <>
              <Textfield
                autoComplete="postal-code"
                defaultValue={state.zip}
                label="Postleitzahl"
                name="zip"
                data-testid="text-zip"
                required
                type="text"
                readOnly={true}
              />

              <Textfield
                label="Ort"
                type="text"
                value={state.city ?? ''}
                readOnly={true}
                required
                data-testid="text-city"
              />
            </>
          )}

          {isEditing && (
            <ZipAndCityInput
              defaultCity={state.city}
              defaultZip={state.zip}
              defaultCityId={state.cityId}
              cityLabel="Ort"
              zipCodeLabel="Postleitzahl"
            />
          )}
        </FormRow>

        <FormRow desktopColumns="2fr-1fr">
          <Textfield
            autoComplete="street-address"
            defaultValue={state.street}
            label="Straße"
            name="street"
            data-testid="text-street"
            required
            type="text"
            readOnly={!isEditing}
            errorMessage={i18n.validation.missing}
          />

          <Textfield
            defaultValue={state.houseNumber}
            label="Hausnummer"
            name="houseNumber"
            data-testid="text-houseNumber"
            pattern={PATTERNS.houseNumber}
            required
            type="text"
            readOnly={!isEditing}
            errorMessage={i18n.validation.missing}
          />
        </FormRow>

        <FormRow desktopColumns="1fr">
          <Textfield
            label="Telefonnummer"
            autoComplete="tel"
            data-testid="text-phone"
            defaultValue={state.phone}
            readOnly={!isEditing}
            name="phone"
            pattern={PATTERNS.phoneNumber}
            required
            type="tel"
            errorMessage={i18n.validation.phone}
          />
        </FormRow>

        <FormRow desktopColumns="1fr">
          <Textfield
            readOnly={!isEditing}
            autoComplete="email"
            data-testid={`text-email`}
            defaultValue={state.email}
            errorMessage={i18n.validation.email}
            label="E-Mail-Adresse"
            name="email"
            pattern={PATTERNS.email}
            required
            type="email"
          />
        </FormRow>

        {editingNotFinishedError && <ErrorBanner>Bitte schließe erst deine Änderungen ab.</ErrorBanner>}
        {actionState?.success === false && <ErrorBanner>{actionState.errorMessage}</ErrorBanner>}

        <div className={styles['button-container']}>
          {isEditing ? (
            <>
              <BoxCTA
                as="button"
                type="button"
                variant="neutral-outline"
                onClick={() => handleFormReset()}
                ariaLabel="Änderungen abbrechen"
                data-testid="button-cancel-edit"
                className={styles['cancel-button']}
              >
                Abbrechen
              </BoxCTA>
              <BoxCTA
                as="button"
                type="submit"
                data-testid="button-save-data"
                ariaLabel="Änderungen speichern"
                className={styles['save-button']}
                loading={isPending}
              >
                Änderung speichern
              </BoxCTA>
            </>
          ) : (
            <BoxCTA
              data-testid="button-edit-data"
              ariaLabel="Daten ändern"
              as="button"
              type="button"
              className={styles['edit-button']}
              onClick={() => setIsEditing(true)}
              variant="neutral-outline"
            >
              Daten ändern
            </BoxCTA>
          )}
        </div>
      </fieldset>
    </form>
  );
};
